import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { CatalogList } from "./catalogList";
import { ProductList } from "./productList";
import { DynoList } from "./dynoList";
import { useLazyQuery, useMutation } from "@apollo/client";
import { SHARE_DYNO_TO_LIST } from "../../graphql/mutations";
import {
  CATALOGS_LIST,
  PRODUCT_LIST,
  PRODUCT_DYNO_LIST,
} from "../../graphql/queries";
import { ModalBox } from "../ModalBox";
import { LoadingOverlay } from "../LoadingOverlay";

export const ShareDynoModal = ({
  selectedDynoId,
  publisherId,
  updateDynoCallback,
  catalogs,
  products,
  dynoList,
  setListCallback,
}) => {
  const [step, setStep] = useState(1);
  const [dynoId, setDynoId] = useState("");
  const [productId, setProductId] = useState("");
  const [listId, setListId] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (selectedDynoId) {
      setDynoId(selectedDynoId);
    }
    if (publisherId) {
      getCatalogs({
        variables: {
          where: {
            id: publisherId,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDynoId, publisherId]);

  const [getCatalogs] = useLazyQuery(CATALOGS_LIST, {
    onCompleted: (data) => {
      setListCallback("catalogs", data.publisher.catalogs);
    },
    onError: () => {},
    fetchPolicy: "no-cache",
  });

  const [getProducts] = useLazyQuery(PRODUCT_LIST, {
    onCompleted: (data) => {
      setListCallback("products", data.catalog.products);
      setStep(2);
    },
    onError: () => {},
    fetchPolicy: "no-cache",
  });

  const [getDynoList] = useLazyQuery(PRODUCT_DYNO_LIST, {
    onCompleted: (data) => {
      if (data.product.productContent) {
        setListCallback("dynoList", data.product.productContent.dynoList);
        setStep(3);
      } else {
        toast({
          position: "top-right",
          title: "No chapter created. Please create chapter first.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      }
    },
    onError: () => {},
    fetchPolicy: "no-cache",
  });

  const [addDynoToDynoList] = useMutation(SHARE_DYNO_TO_LIST, {
    onCompleted: (data) => {
      updateDynoCallback(data.shareDynoToList.dyno);
      setDynoId("");
      setProductId("");
      setListId("");
      setLoading(false);
    },
    onError: () => {
      setDynoId("");
      setProductId("");
      setListId("");
      setLoading(false);
      onOpen();
    },
  });

  const prevStep = () => {
    setStep(step - 1);
  };

  const callback = useCallback((id, type) => {
    if (type === "catalog") {
      getProducts({
        variables: {
          where: {
            id,
          },
        },
      });
    } else if (type === "product") {
      getDynoList({
        variables: {
          where: {
            id,
          },
        },
      });
      setProductId(id);
    } else if (type === "dynoList") {
      setListId(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareDyno = () => {
    if (listId) {
      let index = 0;
      const dynosUnderList = dynoList.find(
        (list) => list.id === listId
      ).dynosUnderList;
      if (dynosUnderList.length > 0) {
        index = dynosUnderList[0].index;
      }
      setLoading(true);
      addDynoToDynoList({
        variables: {
          data: {
            status: "ReadyForQA",
            index: index + 1,
          },
          where: {
            listId: listId,
            productId: productId,
            dynoId: dynoId,
          },
        },
      });
    }
  };

  switch (step) {
    case 1:
      return (
        <CatalogList
          catalogs={catalogs}
          catalogSelectCallback={(id) => callback(id, "catalog")}
          loading={loading}
        />
      );
    case 2:
      return (
        <>
          {loading && <LoadingOverlay />}
          <Box display="flex" alignItems="center" flexDirection="column">
            <ProductList
              products={products}
              productSelectCallback={(id) => callback(id, "product")}
            />
            <Button
              background="#1b6ec2"
              fontWeight="600"
              color="#fff"
              _hover={{
                background: "rgba(27, 110, 194, 0.9)",
              }}
              onClick={prevStep}
              mt="3"
            >
              Previous
            </Button>
          </Box>
        </>
      );
    case 3:
      return (
        <>
          {loading && <LoadingOverlay />}
          <Box display="flex" alignItems="center" flexDirection="column">
            <DynoList
              dynoList={dynoList}
              onListSelectCallback={(id) => callback(id, "dynoList")}
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Button
                background="#1b6ec2"
                fontWeight="600"
                color="#fff"
                _hover={{
                  background: "rgba(27, 110, 194, 0.9)",
                }}
                onClick={prevStep}
                mt="3"
              >
                Previous
              </Button>
              <Button
                background="#1b6ec2"
                fontWeight="600"
                color="#fff"
                disabled={dynoList.length === 0}
                _hover={{
                  background: "rgba(27, 110, 194, 0.9)",
                }}
                onClick={shareDyno}
                mt="3"
              >
                Share
              </Button>
            </Box>
          </Box>
          <ModalBox
            title="Error"
            description={
              <Text fontSize="md" lineHeight="1.5">
                EdDoc has already been shared!
              </Text>
            }
            isOpen={isOpen}
            onClose={onClose}
            isFooter={false}
          ></ModalBox>
        </>
      );
    default:
    // do nothing
  }
};
